import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  FireIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PlusIcon,
  ArrowPathIcon,
  ComputerDesktopIcon,
  UserIcon,
  TruckIcon,
  ClockIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { bulkFuelOperationsService } from '../../services/logistics/bulk-fuel-operations';
import { shipmentCostingsService } from '../../services/logistics/shipment-costings';
import { fuelTransactionsService } from '../../services/logistics/fuel-transactions';
import LoadingSpinner from '../ui/LoadingSpinner';
import VATToggle from '../ui/VATToggle';
import { formatNumber, ensureEnglishNumerals } from '../../utils/formatting';
import { calculateVATBreakdown } from '../../utils/vat-calculations';
import toast from 'react-hot-toast';

const SimplifiedFuelCostSection = ({
  companyId,
  shipmentId,
  onFuelCostUpdate,
  vatPreferences,
  onVATPreferenceUpdate,
  validationErrors = {},
  onValidationErrorUpdate
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [fuelData, setFuelData] = useState(null);
  const [costing, setCosting] = useState(null);
  const [autoApplying, setAutoApplying] = useState(false);
  const [expandedTransactions, setExpandedTransactions] = useState(new Set());

  // Manual fuel state
  const [manualFuelAmount, setManualFuelAmount] = useState('');
  const [manualFuelIncludesVAT, setManualFuelIncludesVAT] = useState(true);

  // Load data
  useEffect(() => {
    loadData();
  }, [companyId, shipmentId]);

  const loadData = async () => {
    if (!companyId || !shipmentId) return;

    try {
      setLoading(true);

      // Get fuel transactions and costing data
      const [overview, currentCosting] = await Promise.all([
        bulkFuelOperationsService.getFuelCostOverview(companyId),
        shipmentCostingsService.getCostingByShipment(shipmentId)
      ]);

      // Filter for this shipment - include both matched transactions and applied transactions
      let shipmentTransactions = overview.transactions.filter(t => t.shipment_id === shipmentId);

      // If we have applied transactions in costing but no visible transactions, 
      // create transaction records from the applied data for display
      if (currentCosting?.appliedFuelTransactions?.length > 0 && shipmentTransactions.length === 0) {
        shipmentTransactions = currentCosting.appliedFuelTransactions.map(appliedTx => ({
          ...appliedTx,
          id: appliedTx.id || `applied_${appliedTx.invoice_number}_${Date.now()}`,
          shipment_id: shipmentId,
          isApplied: true,
          isFromAppliedData: true // Flag to indicate this is reconstructed from applied data
        }));
      } else {
        // Enhanced transaction matching: ensure ALL applied transactions are visible
        if (currentCosting?.appliedFuelTransactions?.length > 0) {
          const appliedMap = new Map();
          const matchedTransactionIds = new Set();
          
          // Create mapping for applied transactions
          currentCosting.appliedFuelTransactions.forEach(appliedTx => {
            const invoiceNumber = appliedTx.invoice_number;
            const fillTime = appliedTx.fill_time;

            if (invoiceNumber && fillTime) {
              // Convert fill_time to consistent format
              let timeKey = '';
              if (fillTime.toDate) {
                timeKey = fillTime.toDate().getTime().toString();
              } else if (fillTime instanceof Date) {
                timeKey = fillTime.getTime().toString();
              } else if (typeof fillTime === 'string') {
                timeKey = new Date(fillTime).getTime().toString();
              } else {
                timeKey = fillTime.toString();
              }

              const primaryKey = `${invoiceNumber}_${timeKey}`;
              const fallbackKey = `${invoiceNumber}_${fillTime}`;
              const invoiceOnlyKey = `invoice_${invoiceNumber}`;

              appliedMap.set(primaryKey, appliedTx);
              appliedMap.set(fallbackKey, appliedTx);
              appliedMap.set(invoiceOnlyKey, appliedTx);
            }
          });

          // Mark existing transactions as applied and track matched ones
          shipmentTransactions = shipmentTransactions.map(tx => {
            const invoiceNumber = tx.invoice_number;
            const fillTime = tx.fill_time;
            let appliedTx = null;

            if (invoiceNumber && fillTime) {
              // Convert fill_time to consistent format
              let timeKey = '';
              if (fillTime.toDate) {
                timeKey = fillTime.toDate().getTime().toString();
              } else if (fillTime instanceof Date) {
                timeKey = fillTime.getTime().toString();
              } else if (typeof fillTime === 'string') {
                timeKey = new Date(fillTime).getTime().toString();
              } else {
                timeKey = fillTime.toString();
              }

              const primaryKey = `${invoiceNumber}_${timeKey}`;
              const fallbackKey = `${invoiceNumber}_${fillTime}`;
              const invoiceOnlyKey = `invoice_${invoiceNumber}`;

              appliedTx = appliedMap.get(primaryKey) ||
                appliedMap.get(fallbackKey) ||
                appliedMap.get(invoiceOnlyKey);
                
              if (appliedTx) {
                matchedTransactionIds.add(appliedTx.invoice_number);
              }
            }

            return {
              ...tx,
              isApplied: !!appliedTx,
              appliedAmount: appliedTx?.cost || tx.cost
            };
          });

          // Add any applied transactions that weren't matched to visible transactions
          const unmatchedAppliedTransactions = currentCosting.appliedFuelTransactions.filter(appliedTx => 
            !matchedTransactionIds.has(appliedTx.invoice_number)
          );

          unmatchedAppliedTransactions.forEach(appliedTx => {
            shipmentTransactions.push({
              ...appliedTx,
              id: appliedTx.id || `applied_${appliedTx.invoice_number}_${Date.now()}`,
              shipment_id: shipmentId,
              isApplied: true,
              isFromAppliedData: true,
              appliedAmount: appliedTx.cost
            });
          });
        }
      }

      // Recompute application status for stats based on current flags to avoid legacy mismatches
      const transactionsForStats = shipmentTransactions.map(tx => ({
        ...tx,
        applicationStatus: !tx.shipment_id
          ? 'unassigned'
          : (tx.isApplied ? 'applied' : 'not_applied')
      }));

      const shipmentStats = bulkFuelOperationsService.calculateOverviewStats(transactionsForStats);

      // Debug logging for transaction matching
      console.log('🔍 Transaction Matching Debug:', {
        shipmentId,
        appliedTransactionsCount: currentCosting?.appliedFuelTransactions?.length || 0,
        visibleTransactionsCount: shipmentTransactions.length,
        appliedTransactions: currentCosting?.appliedFuelTransactions?.map(tx => ({
          invoice_number: tx.invoice_number,
          cost: tx.cost,
          fill_time: tx.fill_time
        })),
        visibleTransactions: shipmentTransactions.map(tx => ({
          invoice_number: tx.invoice_number,
          cost: tx.cost,
          isApplied: tx.isApplied,
          isFromAppliedData: tx.isFromAppliedData
        }))
      });

      setFuelData({
        transactions: shipmentTransactions,
        stats: shipmentStats
      });

      setCosting(currentCosting);

      // Set manual fuel amount if exists
      if (currentCosting?.manualFuelAmount) {
        setManualFuelAmount(currentCosting.manualFuelAmount.toString());
        setManualFuelIncludesVAT(currentCosting.manualFuelIncludesVAT !== false);
      }

    } catch (error) {
      console.error('Error loading fuel data:', error);
      toast.error('Failed to load fuel data');
    } finally {
      setLoading(false);
    }
  };

  // Handle manual fuel change
  const handleManualFuelChange = (value) => {
    setManualFuelAmount(value);
    // Clear validation errors as the user types
    if (onValidationErrorUpdate) {
      onValidationErrorUpdate('manualFuel', null);
    }
  };

  // Persist manual fuel cost only when the user finishes editing,
  // to avoid a Firestore write + parent reload on every keystroke.
  const commitManualFuelChange = () => {
    const numericValue = parseFloat(manualFuelAmount) || 0;
    if (numericValue < 0) return;
    const vatBreakdown = calculateVATBreakdown(numericValue, manualFuelIncludesVAT);
    updateManualFuelCost(numericValue, manualFuelIncludesVAT, vatBreakdown);
  };

  // Handle VAT toggle
  const handleManualFuelVATToggle = async (includesVAT) => {
    setManualFuelIncludesVAT(includesVAT);

    // Update preference
    if (onVATPreferenceUpdate) {
      await onVATPreferenceUpdate('fuel', includesVAT);
    }

    const amount = parseFloat(manualFuelAmount) || 0;
    if (amount > 0) {
      const vatBreakdown = calculateVATBreakdown(amount, includesVAT);
      updateManualFuelCost(amount, includesVAT, vatBreakdown);
    }
  };

  // Update manual fuel cost
  const updateManualFuelCost = async (amount, includesVAT, vatBreakdown) => {
    try {
      const manualFuelData = {
        manualFuelAmount: amount,
        manualFuelIncludesVAT: includesVAT,
        manualFuelBaseAmount: vatBreakdown.baseAmount,
        manualFuelVATAmount: vatBreakdown.vatAmount
      };

      if (costing) {
        await shipmentCostingsService.updateCosting(costing.id, manualFuelData);
      } else if (amount > 0) {
        // Create new costing only if amount > 0
        const newCostingData = {
          companyId,
          shipmentId,
          petroAppFuelAmount: 0,
          petroAppFuelIncludesVAT: true,
          petroAppFuelBaseAmount: 0,
          petroAppFuelVATAmount: 0,
          appliedFuelTransactions: [],
          ...manualFuelData,
          maintenanceAmount: 0,
          commissionAmount: 0,
          stops: [],
          otherCosts: [],
          costingStatus: 'partial',
          createdBy: user.uid
        };

        const newCosting = await shipmentCostingsService.addCosting(newCostingData);
        setCosting({ id: newCosting.id, ...newCostingData });
      }

      // Reload costing data
      const updatedCosting = await shipmentCostingsService.getCostingByShipment(shipmentId);
      setCosting(updatedCosting);

      // Notify parent
      if (onFuelCostUpdate) {
        onFuelCostUpdate();
      }

    } catch (error) {
      console.error('Error updating manual fuel cost:', error);
      toast.error(t('fuel.failed_save_manual'));
    }
  };

  // Auto-apply PetroApp transactions
  const handleAutoApplyPetroApp = async () => {
    if (!fuelData?.transactions.length) return;

    try {
      setAutoApplying(true);

      const unappliedTransactions = fuelData.transactions.filter(t => !t.isApplied);
      if (unappliedTransactions.length === 0) {
        toast.info(t('fuel.all_petroapp_applied'));
        return;
      }

      const transactionIds = unappliedTransactions.map(t => t.id);
      const result = await bulkFuelOperationsService.bulkApplyFuelCosts(
        companyId,
        transactionIds,
        user.uid
      );

      if (result.success > 0) {
        toast.success(t('fuel.applied_petroapp_success', { count: result.success }));
        await loadData(); // Reload all data
      }

      if (result.failed > 0) {
        toast.error(t('fuel.failed_apply_transactions', { count: result.failed }));
      }

    } catch (error) {
      console.error('Error auto-applying PetroApp fuel costs:', error);
      toast.error(t('fuel.failed_auto_apply'));
    } finally {
      setAutoApplying(false);
    }
  };

  // Delete transaction
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm(t('fuel.confirm_delete_transaction'))) return;

    try {
      // Remove from costing first if applied
      await fuelTransactionsService.removeFuelTransactionFromCosting(companyId, transactionId);

      // Delete the transaction
      await fuelTransactionsService.deleteFuelTransaction(transactionId);

      toast.success(t('fuel.transaction_deleted'));
      await loadData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error(t('fuel.failed_delete_transaction'));
    }
  };

  // Format date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Toggle transaction expansion
  const toggleTransactionExpansion = (transactionId) => {
    const newExpanded = new Set(expandedTransactions);
    if (newExpanded.has(transactionId)) {
      newExpanded.delete(transactionId);
    } else {
      newExpanded.add(transactionId);
    }
    setExpandedTransactions(newExpanded);
  };

  // Calculate odometer change
  const calculateOdometerChange = (transaction, allTransactions) => {
    if (!transaction.odometer || !transaction.vehicle_plate) return null;

    // Find previous transaction for same vehicle
    const vehicleTransactions = allTransactions
      .filter(t => t.vehicle_plate === transaction.vehicle_plate && t.fill_time && t.odometer)
      .sort((a, b) => {
        const dateA = a.fill_time.toDate ? a.fill_time.toDate() : new Date(a.fill_time);
        const dateB = b.fill_time.toDate ? b.fill_time.toDate() : new Date(b.fill_time);
        return dateA - dateB;
      });

    const currentIndex = vehicleTransactions.findIndex(t => t.id === transaction.id);
    if (currentIndex > 0) {
      const previousTransaction = vehicleTransactions[currentIndex - 1];
      if (previousTransaction.odometer) {
        return transaction.odometer - previousTransaction.odometer;
      }
    }

    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="flex justify-center">
          <LoadingSpinner size="sm" />
        </div>
      </div>
    );
  }

  const { transactions = [], stats = {} } = fuelData || {};

  // Calculate actual PetroApp amount - prioritize stored costing amount over transaction calculation
  // This ensures consistency and prevents discrepancies from transaction visibility issues
  let actualPetroAppAmount = 0;
  const storedPetroAppAmount = costing?.petroAppFuelAmount || 0;
  
  if (storedPetroAppAmount > 0) {
    // Use stored amount as primary source of truth
    actualPetroAppAmount = storedPetroAppAmount;
  } else {
    // Fallback: calculate from visible transactions if no stored amount
    const appliedVisibleTransactions = transactions.filter(t => t.isApplied);
    
    if (appliedVisibleTransactions.length > 0) {
      // Use only visible applied transactions to calculate total
      actualPetroAppAmount = appliedVisibleTransactions.reduce((sum, tx) => sum + (tx.cost || 0), 0);
    } else if (transactions.length > 0) {
      // Fallback: if no transactions are marked as applied but we have transactions
      const totalTransactionCost = transactions.reduce((sum, tx) => sum + (tx.cost || 0), 0);
      if (totalTransactionCost > 0) {
        actualPetroAppAmount = totalTransactionCost;
      }
    }
  }

  const currentManualFuelAmount = costing?.manualFuelAmount || 0;
  const totalFuelAmount = actualPetroAppAmount + currentManualFuelAmount;

  // Calculate VAT breakdown for manual fuel
  const manualVATBreakdown = calculateVATBreakdown(currentManualFuelAmount, manualFuelIncludesVAT);

  // Debug information
  console.log('🔍 Fuel Data Debug:', {
    shipmentId,
    originalPetroAppAmount: costing?.petroAppFuelAmount || 0,
    actualPetroAppAmount,
    currentManualFuelAmount,
    totalFuelAmount,
    transactionsCount: transactions.length,
    appliedTransactionsCount: costing?.appliedFuelTransactions?.length || 0,
    appliedTransactions: costing?.appliedFuelTransactions,
    visibleTransactions: transactions.map(t => ({
      id: t.id,
      invoice_number: t.invoice_number,
      cost: t.cost,
      isApplied: t.isApplied,
      isFromAppliedData: t.isFromAppliedData
    }))
  });

  return (
    <div className="bg-white rounded-lg border">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FireIcon className="h-5 w-5 text-orange-500" />
            <h3 className="text-lg font-medium text-gray-900">
              {t('fuel.fuel_cost_management')}
            </h3>
          </div>

          {/* Quick Stats */}
          <div className="flex items-center space-x-4 text-sm">
            <div className="text-center">
              <div className="font-bold text-blue-600">{transactions.length}</div>
              <div className="text-gray-500">{t('fuel.transactions')}</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-green-600">{stats.applied || 0}</div>
              <div className="text-gray-500">{t('fuel.applied')}</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-orange-600">{formatNumber(totalFuelAmount)}</div>
              <div className="text-gray-500">ريال</div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Cost Input Fields - Clean 2-column layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* PetroApp Fuel (Read-only with auto-apply) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                <ComputerDesktopIcon className="h-4 w-4 inline mr-2 text-blue-500" />
                {t('fuel.petroapp_fuel_costs')}
              </label>
              {stats.unapplied > 0 && (
                <button
                  onClick={handleAutoApplyPetroApp}
                  disabled={autoApplying}
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                >
                  {autoApplying ? (
                    <LoadingSpinner size="xs" />
                  ) : (
                    <>
                      <ArrowPathIcon className="h-3 w-3 inline mr-1" />
                      {t('fuel.auto_apply_transactions', { count: stats.unapplied })}
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="relative">
              <input
                type="text"
                value={formatNumber(actualPetroAppAmount)}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-blue-50 text-blue-900 font-medium"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <span className="text-gray-500 text-sm">ريال</span>
              </div>
            </div>

            {costing?.appliedFuelTransactions?.length > 0 && (
              <div className="text-xs text-blue-600">
                مطبق من {costing.appliedFuelTransactions.length} معاملة
              </div>
            )}
          </div>

          {/* Manual Fuel (Editable) */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              <UserIcon className="h-4 w-4 inline mr-2 text-green-500" />
              {t('fuel.manual_fuel_costs')}
            </label>

            <div className="relative">
              <input
                type="number"
                value={manualFuelAmount}
                onChange={(e) => handleManualFuelChange(e.target.value)}
                onBlur={(e) => {
                  const cleanValue = ensureEnglishNumerals(e.target.value);
                  if (cleanValue !== manualFuelAmount) {
                    setManualFuelAmount(cleanValue);
                  }
                  commitManualFuelChange();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                className={`w-full py-2 px-3 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 text-right
                  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                  ${validationErrors.manualFuel ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                placeholder={t('fuel.manual_fuel_amount_placeholder')}
                min="0"
                step="0.01"
                dir="rtl"
              />
            </div>

            <VATToggle
              value={manualFuelIncludesVAT}
              onChange={handleManualFuelVATToggle}
              label={t('fuel.amount_includes_vat')}
              costType="manualFuel"
              size="sm"
            />

            {currentManualFuelAmount > 0 && (
              <div className="text-xs text-green-600 space-y-1">
                <div>{t('fuel.base_amount', { amount: formatNumber(manualVATBreakdown.baseAmount) })}</div>
                <div>{t('fuel.vat_amount', { amount: formatNumber(manualVATBreakdown.vatAmount) })}</div>
              </div>
            )}
          </div>
        </div>

        {/* Total Fuel Cost - Compact summary */}
        {totalFuelAmount > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {t('fuel.total_fuel_cost')}:
              </span>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  {formatNumber(totalFuelAmount)} ريال
                </div>
                <div className="text-xs text-gray-500">
                  بترو آب: {formatNumber(actualPetroAppAmount)} + يدوي: {formatNumber(currentManualFuelAmount)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PetroApp Transactions - Clean list */}
        {transactions.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">
                {t('fuel.petroapp_transactions')} ({transactions.length})
              </h4>
            </div>

            <div className="space-y-2">
              {transactions.map((transaction, index) => {
                const isExpanded = expandedTransactions.has(transaction.id);
                const odometerChange = calculateOdometerChange(transaction, transactions);

                return (
                  <div
                    key={transaction.id || index}
                    className={`rounded-lg border ${transaction.isApplied
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                      }`}
                  >
                    {/* Main transaction row */}
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {transaction.isApplied ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          ) : (
                            <ClockIcon className="h-5 w-5 text-orange-500" />
                          )}
                        </div>

                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{transaction.invoice_number || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center space-x-2">
                            <TruckIcon className="h-3 w-3" />
                            <span>{transaction.mappedVehiclePlate || transaction.vehicle_plate}</span>
                            <span>•</span>
                            <span>{formatDateTime(transaction.fill_time)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {formatNumber(transaction.cost || 0)} ريال
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatNumber(transaction.liters || 0)} لتر
                          </div>
                        </div>

                        <button
                          onClick={() => toggleTransactionExpansion(transaction.id)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="عرض التفاصيل"
                        >
                          {isExpanded ? (
                            <ChevronUpIcon className="h-4 w-4" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </button>

                        <button
                          onClick={() => handleDeleteTransaction(transaction.id)}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title={t('fuel.delete_transaction')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-gray-200 bg-white/50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 text-xs">
                          {/* Odometer Reading */}
                          {transaction.odometer && (
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <CogIcon className="h-3 w-3 text-blue-500" />
                              <div>
                                <div className="text-gray-500">عداد المسافات</div>
                                <div className="font-medium">{formatNumber(transaction.odometer)} كم</div>
                              </div>
                            </div>
                          )}

                          {/* Odometer Change */}
                          {odometerChange && (
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <ArrowPathIcon className="h-3 w-3 text-green-500" />
                              <div>
                                <div className="text-gray-500">المسافة المقطوعة</div>
                                <div className="font-medium text-green-600">+{formatNumber(odometerChange)} كم</div>
                              </div>
                            </div>
                          )}

                          {/* Driver */}
                          {transaction.driver && (
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <UserIcon className="h-3 w-3 text-indigo-500" />
                              <div>
                                <div className="text-gray-500">السائق</div>
                                <div className="font-medium">{transaction.driver}</div>
                              </div>
                            </div>
                          )}

                          {/* Vehicle Mapping */}
                          {transaction.vehicle_plate !== transaction.mappedVehiclePlate && transaction.mappedVehiclePlate && (
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <TruckIcon className="h-3 w-3 text-purple-500" />
                              <div>
                                <div className="text-gray-500">ربط المركبة</div>
                                <div className="font-medium">
                                  <div className="text-gray-600">{transaction.vehicle_plate}</div>
                                  <div className="text-purple-600">← {transaction.mappedVehiclePlate}</div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Station Location */}
                          {(transaction.station_name || transaction.station) && (
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <MapPinIcon className="h-3 w-3 text-orange-500" />
                              <div>
                                <div className="text-gray-500">المحطة</div>
                                <div className="font-medium">{transaction.station_name || transaction.station}</div>
                              </div>
                            </div>
                          )}

                          {/* Fuel Efficiency */}
                          {odometerChange && transaction.liters && odometerChange > 0 && (
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <FireIcon className="h-3 w-3 text-red-500" />
                              <div>
                                <div className="text-gray-500">استهلاك الوقود</div>
                                <div className="font-medium">
                                  {(odometerChange / transaction.liters).toFixed(2)} كم/لتر
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Cost per Liter */}
                          {transaction.liters && transaction.cost && (
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <BanknotesIcon className="h-3 w-3 text-green-500" />
                              <div>
                                <div className="text-gray-500">سعر اللتر</div>
                                <div className="font-medium">
                                  {(transaction.cost / transaction.liters).toFixed(2)} ريال/لتر
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Station Coordinates */}
                          {transaction.station_coordinates && (
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <MapPinIcon className="h-3 w-3 text-blue-500" />
                              <div>
                                <div className="text-gray-500">الإحداثيات</div>
                                <div className="font-medium text-xs">
                                  {transaction.station_coordinates.lat.toFixed(4)}, {transaction.station_coordinates.lng.toFixed(4)}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Applied Status */}
                        {transaction.isApplied && (
                          <div className="mt-3 pt-2 border-t border-green-200">
                            <div className="flex items-center space-x-2 rtl:space-x-reverse text-xs text-green-600">
                              <CheckCircleIcon className="h-3 w-3" />
                              <span>مطبق على تكلفة الشحنة</span>
                              {transaction.isFromAppliedData && (
                                <span className="text-blue-600">(من البيانات المطبقة)</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {transactions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <ComputerDesktopIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-sm">{t('fuel.no_petroapp_data')}</p>
            <p className="text-xs mt-1">{t('fuel.import_excel_automatically')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimplifiedFuelCostSection;